import { supabase } from "@/integrations/supabase/client";
import { isNativeApp } from "@/lib/nativeApp";

// ─── Helpers ────────────────────────────────────────────────────────────

function bufferToBase64url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let str = "";
  bytes.forEach((b) => (str += String.fromCharCode(b)));
  return btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function base64urlToBuffer(base64url: string): ArrayBuffer {
  const base64 = base64url.replace(/-/g, "+").replace(/_/g, "/");
  const pad = base64.length % 4 === 0 ? "" : "=".repeat(4 - (base64.length % 4));
  const binary = atob(base64 + pad);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

const RP_NAME = "Behtari";
const RP_ID = typeof window !== "undefined" ? window.location.hostname : "localhost";

type BiometricOperation = "create" | "verify";
type WebAuthnUnavailableReason = "unsupported" | "insecure_context" | "permissions_policy";

// ─── Account-Scoped Storage ────────────────────────────────────────────

const STORAGE_KEY = "behtari_bio_enrollment";

interface BiometricEnrollment {
  userId: string;
  authHint: string; // CNIC or email for display
  credentialId: string;
  refreshToken: string;
}

function getEnrollment(): BiometricEnrollment | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.userId || !parsed?.credentialId) return null;
    return parsed as BiometricEnrollment;
  } catch {
    return null;
  }
}

function setEnrollment(enrollment: BiometricEnrollment): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(enrollment));
}

function clearEnrollment(): void {
  localStorage.removeItem(STORAGE_KEY);
  // Clean up legacy keys
  localStorage.removeItem("behtari_bio_refresh");
  localStorage.removeItem("behtari_bio_cred_id");
  localStorage.removeItem("behtari_bio_user_email");
}

// ─── WebAuthn Support ───────────────────────────────────────────────────

function getPermissionsPolicyObject(): any | null {
  if (typeof document === "undefined") return null;
  return (document as any).permissionsPolicy || (document as any).featurePolicy || null;
}

function isWebAuthnBlockedByPermissionsPolicy(): boolean {
  const policy = getPermissionsPolicyObject();
  if (!policy || typeof policy.allowsFeature !== "function") return false;
  const createAllowed = policy.allowsFeature("publickey-credentials-create");
  const getAllowed = policy.allowsFeature("publickey-credentials-get");
  return !createAllowed || !getAllowed;
}

export function getWebAuthnUnavailableReason(): WebAuthnUnavailableReason | null {
  if (typeof window === "undefined") return "unsupported";
  if (!window.isSecureContext) return "insecure_context";
  if (!window.PublicKeyCredential || typeof window.PublicKeyCredential !== "function") return "unsupported";
  if (isWebAuthnBlockedByPermissionsPolicy()) return "permissions_policy";
  return null;
}

export function isWebAuthnSupported(): boolean {
  return getWebAuthnUnavailableReason() === null;
}

export async function isPlatformAuthenticatorAvailable(): Promise<boolean> {
  if (!isWebAuthnSupported()) return false;
  try {
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

function mapBiometricError(error: unknown, operation: BiometricOperation): Error {
  const err = error as DOMException & { message?: string };
  const message = err?.message?.toLowerCase?.() || "";

  if (
    err?.name === "SecurityError" ||
    message.includes("permissions policy") ||
    message.includes("publickey-credentials-create") ||
    message.includes("publickey-credentials-get")
  ) {
    return new Error(
      operation === "create"
        ? "Biometric setup is blocked in this preview frame. Open the app directly and try again."
        : "Biometric sign-in is blocked in this preview frame. Open the app directly and try again."
    );
  }

  if (err?.name === "NotAllowedError") {
    return new Error(
      operation === "create"
        ? "Biometric setup was cancelled or denied on this device."
        : "Biometric verification was cancelled or denied."
    );
  }

  if (err?.name === "NotSupportedError") {
    return new Error("Biometric authentication is not supported on this device/browser.");
  }

  return new Error(
    err?.message ||
      (operation === "create"
        ? "Could not set up biometrics on this device."
        : "Could not verify biometrics on this device.")
  );
}

// ─── Capacitor Biometrics ───────────────────────────────────────────────

export async function isNativeBiometricAvailable(): Promise<boolean> {
  try {
    const { NativeBiometric } = await import("capacitor-native-biometric");
    const result = await NativeBiometric.isAvailable();
    return result.isAvailable;
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn("[Biometric] isNativeBiometricAvailable check failed:", err);
    }
    return false;
  }
}

// ─── Registration (Enroll biometrics) ───────────────────────────────────

export async function registerBiometric(deviceName?: string): Promise<boolean> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("Not authenticated");

  // ── Native path (Capacitor Android / iOS) ─────────────────────────────
  // WebAuthn (navigator.credentials.create) is NOT supported in the Android
  // WebView that Capacitor uses — only in full Chrome. Use the native
  // biometric plugin instead, which talks directly to the device keystore.
  if (isNativeApp()) {
    try {
      const { NativeBiometric } = await import("capacitor-native-biometric");
      const { isAvailable } = await NativeBiometric.isAvailable();
      if (!isAvailable) {
        throw new Error(
          "Biometric authentication is not available on this device. " +
          "Please set up a fingerprint or face in your Android Settings → " +
          "Security → Biometrics first."
        );
      }

      const { data: sessionData } = await supabase.auth.getSession();
      const refreshToken = sessionData?.session?.refresh_token;
      if (!refreshToken) {
        throw new Error("Session not found. Please sign in with your password first.");
      }

      const authHint = user.email || user.id.slice(0, 8);
      const credentialId = `native-${user.id}`;

      setEnrollment({
        userId: user.id,
        authHint,
        credentialId,
        refreshToken,
      });

      // Record in webauthn_credentials for display in settings (device list).
      // upsert so re-enrolling the same device doesn't create duplicates.
      await supabase.from("webauthn_credentials").upsert(
        {
          user_id: user.id,
          credential_id: credentialId,
          public_key: "native-biometric",
          counter: 0,
          device_name: deviceName || detectDeviceName(),
        },
        { onConflict: "user_id,credential_id" }
      );

      return true;
    } catch (err: any) {
      // Re-throw errors we deliberately crafted above; wrap anything else.
      if (
        err.message?.includes("not available") ||
        err.message?.includes("Session not found") ||
        err.message?.includes("Biometric")
      ) {
        throw err;
      }
      throw new Error("Could not set up biometrics on this device. Please try again.");
    }
  }
  // ── End native path ───────────────────────────────────────────────────

  if (!isWebAuthnSupported()) {
    const reason = getWebAuthnUnavailableReason();
    if (reason === "permissions_policy") {
      throw new Error("Biometric setup is blocked in this preview frame. Open the app directly and try again.");
    }
    throw new Error("Biometric authentication is not available on this device/browser.");
  }

  const challenge = crypto.getRandomValues(new Uint8Array(32));
  const userId = new TextEncoder().encode(user.id);

  const createOptions: PublicKeyCredentialCreationOptions = {
    challenge,
    rp: { name: RP_NAME, id: RP_ID },
    user: {
      id: userId,
      name: user.email || user.id,
      displayName: user.email || "Behtari User",
    },
    pubKeyCredParams: [
      { alg: -7, type: "public-key" },
      { alg: -257, type: "public-key" },
    ],
    authenticatorSelection: {
      authenticatorAttachment: "platform",
      userVerification: "required",
      residentKey: "preferred",
    },
    timeout: 60000,
    attestation: "none",
  };

  let credential: PublicKeyCredential | null = null;
  try {
    credential = (await navigator.credentials.create({
      publicKey: createOptions,
    })) as PublicKeyCredential | null;
  } catch (error) {
    throw mapBiometricError(error, "create");
  }

  if (!credential) throw new Error("Biometric setup was cancelled.");

  const response = credential.response as AuthenticatorAttestationResponse;
  const credentialId = bufferToBase64url(credential.rawId);
  const publicKey = bufferToBase64url(response.getPublicKey?.() || new ArrayBuffer(0));

  const { error } = await supabase.from("webauthn_credentials").insert({
    user_id: user.id,
    credential_id: credentialId,
    public_key: publicKey || "platform-authenticator",
    counter: 0,
    device_name: deviceName || detectDeviceName(),
  });

  if (error) throw error;

  // Get current session refresh token and store account-scoped enrollment
  const { data: session } = await supabase.auth.getSession();
  const authHint = user.email || user.id.slice(0, 8);

  setEnrollment({
    userId: user.id,
    authHint,
    credentialId,
    refreshToken: session?.session?.refresh_token || "",
  });

  return true;
}

// ─── Authentication (Verify biometrics to sign in) ──────────────────────

export async function authenticateWithBiometric(): Promise<boolean> {
  const enrollment = getEnrollment();

  if (!enrollment) {
    throw new Error("No biometric enrollment found. Please enable biometrics in Settings first.");
  }

  // 1. Check WebAuthn support first (cheap check, no user interaction)
  if (!isWebAuthnSupported()) {
    const reason = getWebAuthnUnavailableReason();
    if (reason === "permissions_policy") {
      throw new Error("Biometric sign-in is blocked in this preview frame. Open the app directly and try again.");
    }
    throw new Error("Biometric sign-in is not available on this device/browser.");
  }

  // 2. Show biometric prompt FIRST — let the user verify identity before
  //    we check the refresh token. This ensures the fingerprint/face dialog
  //    always appears even if the token was previously cleared.
  const challenge = crypto.getRandomValues(new Uint8Array(32));

  const getOptions: PublicKeyCredentialRequestOptions = {
    challenge,
    rpId: RP_ID,
    allowCredentials: [
      {
        id: base64urlToBuffer(enrollment.credentialId),
        type: "public-key",
        transports: ["internal"],
      },
    ],
    userVerification: "required",
    timeout: 60000,
  };

  let assertion: Credential | null = null;
  try {
    assertion = await navigator.credentials.get({
      publicKey: getOptions,
    });
  } catch (error) {
    throw mapBiometricError(error, "verify");
  }

  if (!assertion) throw new Error("Biometric verification failed");

  // 3. NOW check/recover the refresh token (identity already proven)
  if (!enrollment.refreshToken) {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user.id === enrollment.userId && session.refresh_token) {
      enrollment.refreshToken = session.refresh_token;
      setEnrollment(enrollment);
      if (import.meta.env.DEV) console.log("[Biometric] Recovered refresh token from active session");
    } else {
      throw new Error(
        "Biometric session expired. Please sign in once with your password to reactivate biometric login."
      );
    }
  }

  // Refresh the session using stored token
  const { data, error } = await supabase.auth.refreshSession({
    refresh_token: enrollment.refreshToken,
  });

  if (error || !data.session) {
    // Self-healing: try to recover from an active session before giving up
    const { data: { session: activeSession } } = await supabase.auth.getSession();
    if (activeSession?.user?.id === enrollment.userId && activeSession.refresh_token) {
      // Re-try refresh with the active session's token
      const { data: retryData, error: retryError } = await supabase.auth.refreshSession({
        refresh_token: activeSession.refresh_token,
      });
      if (!retryError && retryData.session) {
        setEnrollment({ ...enrollment, refreshToken: retryData.session.refresh_token });
        if (import.meta.env.DEV) console.log("[Biometric] Self-healed from active session");
        // Continue to mismatch guard below with retryData
        return handleBiometricSessionSuccess(retryData.session, enrollment);
      }
    }

    // Clear only the refresh token, keep enrollment intact
    setEnrollment({ ...enrollment, refreshToken: "" });
    throw new Error(
      `Biometric session expired for ${enrollment.authHint}. Sign in once with password to reactivate biometric login.`
    );
  }

  return handleBiometricSessionSuccess(data.session, enrollment);
}

/** Shared post-refresh logic for biometric auth */
async function handleBiometricSessionSuccess(
  session: { user: { id: string }; refresh_token: string },
  enrollment: BiometricEnrollment
): Promise<boolean> {
  // Mismatch guard: ensure refreshed session matches enrolled user
  if (session.user.id !== enrollment.userId) {
    setEnrollment({ ...enrollment, refreshToken: "" });
    throw new Error(
      `Biometric is enrolled for a different account (${enrollment.authHint}). Sign in with password for that account first.`
    );
  }

  // Update stored refresh token
  setEnrollment({ ...enrollment, refreshToken: session.refresh_token });

  // Update last_used_at
  await supabase
    .from("webauthn_credentials")
    .update({ last_used_at: new Date().toISOString() })
    .eq("credential_id", enrollment.credentialId)
    .eq("user_id", session.user.id);

  return true;
}

// ─── Native biometric authentication ────────────────────────────────────

export async function authenticateWithNativeBiometric(): Promise<boolean> {
  const enrollment = getEnrollment();
  if (!enrollment) {
    throw new Error("No biometric enrollment found. Please enable biometrics in Settings first.");
  }
  if (!enrollment.refreshToken) {
    // Try to recover from an active session before failing
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user.id === enrollment.userId && session.refresh_token) {
      enrollment.refreshToken = session.refresh_token;
      setEnrollment(enrollment);
    } else {
      throw new Error(
        `Biometric is enrolled for ${enrollment.authHint}. Sign in once with password to activate biometric login.`
      );
    }
  }

  try {
    const { NativeBiometric } = await import("capacitor-native-biometric");
    await NativeBiometric.verifyIdentity({
      reason: "Sign in to Behtari",
      title: "Biometric Authentication",
      subtitle: "Use your fingerprint or face to sign in",
      negativeButtonText: "Cancel",
    });

    const { data, error } = await supabase.auth.refreshSession({
      refresh_token: enrollment.refreshToken,
    });

    if (error || !data.session) {
      setEnrollment({ ...enrollment, refreshToken: "" });
      throw new Error(
        `Biometric session expired for ${enrollment.authHint}. Sign in once with password to reactivate.`
      );
    }

    if (data.session.user.id !== enrollment.userId) {
      setEnrollment({ ...enrollment, refreshToken: "" });
      throw new Error("Biometric is enrolled for a different account.");
    }

    setEnrollment({ ...enrollment, refreshToken: data.session.refresh_token });
    return true;
  } catch (err: any) {
    if (err.message?.includes("session expired") || err.message?.includes("enrolled for")) throw err;
    throw new Error("Biometric verification failed");
  }
}

// ─── Utility ────────────────────────────────────────────────────────────

/** Returns true if there is a biometric enrollment stored on this device */
export function hasBiometricEnrollment(): boolean {
  return getEnrollment() !== null;
}

/** Returns the enrolled account hint (CNIC/email) for display purposes */
export function getEnrolledAccountHint(): string | null {
  return getEnrollment()?.authHint || null;
}

/** Returns true if enrollment exists (even if refresh token is empty) */
export function hasBiometricCredentials(): boolean {
  return hasBiometricEnrollment();
}

/**
 * Sync refresh token for the matching enrolled user.
 * Called on password login and token refresh events.
 * Only updates if the provided userId matches the enrolled userId.
 */
export function syncBiometricSessionToken(userId: string, refreshToken: string | null | undefined): void {
  if (!refreshToken) return;
  const enrollment = getEnrollment();
  if (!enrollment) return;
  // Only sync if the session user matches the enrolled user
  if (enrollment.userId !== userId) return;
  setEnrollment({ ...enrollment, refreshToken });
}

export function clearBiometricSessionToken(): void {
  const enrollment = getEnrollment();
  if (enrollment) {
    setEnrollment({ ...enrollment, refreshToken: "" });
  }
}

export function clearBiometricData(): void {
  clearEnrollment();
}

export async function removeBiometric(): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (user) {
    await supabase
      .from("webauthn_credentials")
      .delete()
      .eq("user_id", user.id);
  }
  clearEnrollment();
}

export async function getBiometricCredentials() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];

  const { data } = await supabase
    .from("webauthn_credentials")
    .select("*")
    .eq("user_id", user.id)
    .order("created_at", { ascending: false });

  return data || [];
}

function detectDeviceName(): string {
  const ua = navigator.userAgent;
  if (/iPhone/.test(ua)) return "iPhone";
  if (/iPad/.test(ua)) return "iPad";
  if (/Android/.test(ua)) return "Android Device";
  if (/Mac/.test(ua)) return "Mac";
  if (/Windows/.test(ua)) return "Windows PC";
  return "Unknown Device";
}
